import React, { useEffect, useMemo, useState } from 'react';
import { X, FloppyDisk } from '@phosphor-icons/react';
import { Input, Spin, Button, notification, Tabs } from 'antd';
import DashboardModal from './DashboardModal';
import designServices from '../../../../services/oneButtonServices/designServices';
import masterServices from '../../../../services/oneButtonServices/masterServices';
import { OBDSelect } from './form-elements';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import './oneButton-css/UtilityLoadCalculatorModal.css';

interface LoadCalculatorItem {
  OPLCId: number | null;
  LCTypeId: number;
  LCTypeName: string;
  MonthlyKWH: number;
  ProductLoadValue: number | null;
}

interface EVLoadCalculatorItem {
  EV_Number: number;
  EV_Manufacturer_Name: string;
  EV_Model_Name: string;
  Avg_DailyMiles_Driven: number;
  Annual_Miles: number;
  Home_Charging_Perc: number;
  Daily_kWh: number;
  Annual_kWh: number;
}

const DEFAULT_EV = (): EVLoadCalculatorItem => ({
  EV_Number: 1,
  EV_Manufacturer_Name: '',
  EV_Model_Name: '',
  Avg_DailyMiles_Driven: 0,
  Annual_Miles: 0,
  Home_Charging_Perc: 0,
  Daily_kWh: 0,
  Annual_kWh: 0,
});

interface UtilityLoadCalculatorModalProps {
  open: boolean;
  onClose: () => void;
  opportunityId?: number;
}

const UtilityLoadCalculatorModal: React.FC<UtilityLoadCalculatorModalProps> = ({
  open,
  onClose,
  opportunityId,
}) => {
  const theme = useOneButtonStore((s) => s.theme);
  const solarUtilityData = useOneButtonStore((s) => s.solarUtilityData);
  const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadCalculator, setLoadCalculator] = useState<LoadCalculatorItem[]>([]);
  const [noticeApi, noticeContext] = notification.useNotification({ maxCount: 4 });
/* ───────────── EV STATE ───────────── */
  const [activeEVIndex, setActiveEVIndex] = useState(0);

  const [evLoadCalculator, setEvLoadCalculator] = useState<EVLoadCalculatorItem[]>([ DEFAULT_EV() ]);

  const [vehicleManufacturers, setVehicleManufacturers] = useState<string[]>([]);
  const [percentChargedOptions, setPercentChargedOptions] = useState<string[]>([]);
  const [evModels, setEvModels] = useState<string[][]>([]);
  const [vehicleModelsCache, setVehicleModelsCache] = useState<Record<string, string[]>>({});

  /* ───────────── EV CALCULATION ───────────── */
  const calculateEVUsage = async () => {
    const validVehicles = evLoadCalculator.filter(
      v =>
        v.EV_Manufacturer_Name &&
        v.EV_Model_Name &&
        v.Avg_DailyMiles_Driven > 0 &&
        v.Home_Charging_Perc >= 0
    );

    if (!validVehicles.length) return;

    const v1 = validVehicles[0];
    const v2 = validVehicles[1];

    const electricVehicle = {
      vehicle1Manufacturer: v1.EV_Manufacturer_Name,
      vehicle1Model: v1.EV_Model_Name,
      vehicle1DailyMiles: String(v1.Avg_DailyMiles_Driven),
      vehicle1PercentCharged: String(v1.Home_Charging_Perc),

      vehicle2Manufacturer: v2?.EV_Manufacturer_Name || v1.EV_Manufacturer_Name,
      vehicle2Model: v2?.EV_Model_Name || v1.EV_Model_Name,
      vehicle2DailyMiles: v2 ? String(v2.Avg_DailyMiles_Driven) : "0",
      vehicle2PercentCharged: v2 ? String(v2.Home_Charging_Perc) : String(v1.Home_Charging_Perc),
    };

    const payload = {
      ExcelFileName: 'Consumption Calculator',
      SheetName: 'Consumption Calculator IO',
      InputValues: [
        { Type: 'string', CellNumber: 'C4', CellValue: v1.EV_Manufacturer_Name },
        { Type: 'string', CellNumber: 'C5', CellValue: v2?.EV_Manufacturer_Name || '' },
        { Type: 'string', CellNumber: 'C7', CellValue: JSON.stringify({ electricVehicle }) },
      ],
      OutputCells: ['F7'],
    };

    const outputValues = await masterServices.cascadingDropDown(payload);
    if (!outputValues?.length) return;

    const parsed = JSON.parse(outputValues[0].CellValue || '{}');
    const results = parsed?.output?.electricVehicles;
    if (!results) return;

    setEvLoadCalculator(prev =>
      prev.map((ev, idx) => {
        const r = results[`vehicle${idx + 1}`];
        return r
          ? { ...ev, Daily_kWh: Number(r.dailyKWHs || 0), Annual_kWh: Number(r.annualKWHs || 0) } : ev;
      })
    );
  };


  /* ───────────── Load EV Master Data (NO REFACTOR) ───────────── */
  useEffect(() => {
    if (!open) return;

    masterServices.cascadingDropDown({
      ExcelFileName: 'Consumption Calculator',
      SheetName: 'Consumption Calculator IO',
      InputValues: [],
      OutputCells: ['F3', 'F6'],
    }).then((outputValues: any[]) => {
      const manufacturerJson = JSON.parse(outputValues[0]?.CellValue || '{}');
      const percentJson = JSON.parse(outputValues[1]?.CellValue || '{}');

      setVehicleManufacturers(manufacturerJson.vehicleManufacturerOptions || []);
      setPercentChargedOptions(percentJson.percentChargedOptions || []);
    });
  }, [open]);

  useEffect(() => {
    const ev = evLoadCalculator[activeEVIndex];
    if (!ev?.EV_Manufacturer_Name) return;

    loadEVModels(ev.EV_Manufacturer_Name, activeEVIndex);
  }, [ activeEVIndex, evLoadCalculator[activeEVIndex]?.EV_Manufacturer_Name ]);

  /* ───────────── Retrieve Appliance Load Calculator ───────────── */
  useEffect(() => {
    if (!open || !opportunityId) return;

    setLoading(true);
    designServices
      .RetrieveLoadCalculator(opportunityId)
      .then((res: any) => {
        setLoadCalculator(res?.LoadCalculator || []);

        const evs = Array.isArray(res?.EVLoadCalculator) && res.EVLoadCalculator.length
            ? res.EVLoadCalculator.map((v: any, i: number) => ({ ...v, EV_Number: i + 1, })) : [DEFAULT_EV()];

        setEvLoadCalculator(evs);
        setActiveEVIndex(0);
      })
      .finally(() => setLoading(false));
  }, [open, opportunityId]);

  // ✅ FIX: calculate for the ACTIVE vehicle immediately
  useEffect(() => {
    const ev = evLoadCalculator[activeEVIndex];
    if (!ev) return;

    const isComplete =
      ev.EV_Manufacturer_Name &&
      ev.EV_Model_Name &&
      ev.Avg_DailyMiles_Driven > 0 &&
      ev.Home_Charging_Perc >= 0 &&
      ev.Annual_Miles > 0;

    if (!isComplete) return;

    calculateEVUsage();
  }, [
    activeEVIndex, 
    evLoadCalculator[activeEVIndex]?.EV_Manufacturer_Name,
    evLoadCalculator[activeEVIndex]?.EV_Model_Name,
    evLoadCalculator[activeEVIndex]?.Avg_DailyMiles_Driven,
    evLoadCalculator[activeEVIndex]?.Home_Charging_Perc,
    evLoadCalculator[activeEVIndex]?.Annual_Miles,
  ]);

  /* ───────────── EV helpers ───────────── */
  const updateEV = (index: number, changes: Partial<EVLoadCalculatorItem>) => {
    setEvLoadCalculator(prev => {
      const next = [...prev];
      const current = next[index];

      const nextItem = { ...current, ...changes };

      const avgDaily = nextItem.Avg_DailyMiles_Driven || 0;

      nextItem.Annual_Miles = avgDaily * 260;

      if (!nextItem.EV_Manufacturer_Name || !nextItem.EV_Model_Name) {
        nextItem.Daily_kWh = 0;
        nextItem.Annual_kWh = 0;
      } else {
        const homePct = nextItem.Home_Charging_Perc || 0;
        const baseDaily = nextItem.Daily_kWh || 0;

        nextItem.Daily_kWh = baseDaily;
        nextItem.Annual_kWh = baseDaily * 260 * (homePct / 100);
      }

      next[index] = nextItem;
      return next;
    });
  };

  const addVehicle = () => {
    setEvLoadCalculator(prev => {
      if (prev.length >= 2) return prev;
      return [...prev, { ...DEFAULT_EV(), EV_Number: 2 }];
    });

    setActiveEVIndex(1);
  };

  const loadEVModels = async (manufacturer: string, index: number) => {
    if (!manufacturer) return;

    // ✅ Cache hit
    if (vehicleModelsCache[manufacturer]) {
      setEvModels(prev => {
        const next = [...prev];
        next[index] = vehicleModelsCache[manufacturer];
        return next;
      });
      return;
    }

    const inputCell = index === 0 ? 'C4' : 'C5';
    const outputCell = index === 0 ? 'F4' : 'F5';

    const outputValues = await masterServices.cascadingDropDown({
      ExcelFileName: 'Consumption Calculator',
      SheetName: 'Consumption Calculator IO',
      InputValues: [ { Type: 'string', CellNumber: inputCell, CellValue: manufacturer } ],
      OutputCells: [outputCell],
    });

    const parsed = JSON.parse(outputValues?.[0]?.CellValue || '{}');

    const models = index === 0 ? parsed.vehicle1ModelOptions || [] : parsed.vehicle2ModelOptions || [];

    // ✅ Cache per manufacturer
    setVehicleModelsCache(prev => ({ ...prev, [manufacturer]: models }));

    setEvModels(prev => {
      const next = [...prev];
      next[index] = models;
      return next;
    });
  };

  /* ───────────── Totals ───────────── */
  const applianceTotal = useMemo(() => {
    return loadCalculator.reduce((sum, item) => {
      const qty = Number(item.ProductLoadValue) || 0;
      return sum + qty * item.MonthlyKWH;
    }, 0);
  }, [loadCalculator]);

  const evTotal = useMemo(() => {
    return evLoadCalculator.reduce((sum, ev) => sum + (Number(ev.Annual_kWh) || 0), 0);
  }, [evLoadCalculator]);

  const totalAnnualKwh = applianceTotal + evTotal;

  /* ───────────── Save ───────────── */
  const onSave = async () => {
    if (!opportunityDetails) return;

    setSaving(true);
    try {
      await designServices.SaveProductConfigLoadCalculator({
        ...solarUtilityData,
        Opportunity_Id: opportunityDetails.Opportunity_Id,
        SolarUtility: { ...solarUtilityData?.SolarUtility, SolarLoadCalcTotal: Math.round(totalAnnualKwh) },
        LoadCalculator: loadCalculator,
        EVLoadCalculator: evLoadCalculator,
      });

      noticeApi.success({ message: 'Load Calculator', description: 'Load calculator saved successfully.', duration: 2 });
      setTimeout(() => {onClose();},1000);
    } catch {
      noticeApi.error({ message: 'Load Calculator', description: 'Failed to save load calculator.', duration: 2 });
      setTimeout(onClose, 700);
    } finally {
      setSaving(false);
    }
  };

  /* ───────────── Render ───────────── */
  return (
    <DashboardModal
      open={open}
      onClose={() => !saving && onClose()}
      title="Utility Load Calculator"
      width={1100}
      centered
      destroyOnClose
      maskClosable={false}
      theme={theme}
      className={`obd-load-calculator-modal obd-theme-${theme}`}
      footer={
        <div className="obd-load-calc-modal__footer-inner">
          {!loading ? (
            <div className="obd-load-calc__footer-total" aria-live="polite">
              <span className="obd-load-calc__footer-total-label">Estimated Annual Usage</span>
              <span className="obd-load-calc__footer-total-value">
                {totalAnnualKwh.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                kWh
              </span>
            </div>
          ) : null}
          <div className="obd-load-calc-modal__footer-actions obd-utility-details-modal__footer-actions">
            <Button className="obd-utility-details-modal__btn-close" onClick={onClose} disabled={saving}>
              <X size={14} weight="bold" aria-hidden />
              Close
            </Button>

            <Button className="obd-utility-details-modal__btn-save" type="primary" loading={saving} onClick={onSave} disabled={saving}>
              {!saving && <FloppyDisk size={14} weight="fill" aria-hidden />}
              Save
            </Button>
          </div>
        </div>
      }
    >
      {noticeContext}

      {loading ? (
        <div style={{ minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
          <Spin />
          <span>Loading load calculator…</span>
        </div>
      ) : (
        <div className="obd-contact-modal__inner">
          <div className="obd-load-calc-section">

          {/* Appliances */}
          <div className="obd-ev-section__header">
            <h3 className="obd-contact-modal__section-title">Appliance Load Inputs</h3>
          </div>

          <div className="obd-load-calc__grid">
            {loadCalculator.map((item, index) => (
              <div key={item.LCTypeId} className="obd-load-calc__field">
                <label className="obd-load-grid_label">{item.LCTypeName} :</label>
                <Input type="number" min={0} value={item.ProductLoadValue ?? ''} className="obd-contact-modal__input"
                  onChange={(e) =>
                    setLoadCalculator((prev) => {
                      const next = [...prev];
                      next[index].ProductLoadValue = e.target.value === '' ? null : Number(e.target.value);
                      return next;
                    }) 
                    } />
              </div>
            ))}
          </div>
          </div>

          {/* EV SECTION */}
          <div className="obd-ev-section">
            <div className="obd-ev-section__header">
              <h3 className="obd-contact-modal__section-title">
                Electric Vehicle
              </h3>
            </div>
            <Tabs activeKey={String(activeEVIndex)} onChange={(key) => setActiveEVIndex(Number(key))}
              tabBarExtraContent={
                <Button type="text" onClick={addVehicle} disabled={evLoadCalculator.length >= 2}>
                  + Add Vehicle
                </Button> }
            >
              {evLoadCalculator.map((ev, index) => (
                <Tabs.TabPane
                  key={String(index)}
                  tab={
                    <div className="obd-ev-tab">
                      <span>Vehicle {index + 1}</span>

                      {index === 1 && (
                        <X size={14} weight="bold" className="obd-ev-tab__close" 
                          onClick={(e) => { 
                            e.stopPropagation();

                            setEvLoadCalculator(prev => prev.slice(0, 1));
                            setActiveEVIndex(0);
                          }}
                        />
                      )}
                    </div>
                  }
                >
                {/* EV FORM CONTENT (unchanged) */}
                <div className="obd-load-calc__grid">
                  <div className="obd-load-calc__field">
                    <label className="obd-load-ev_label">Manufacturer</label>
                    <OBDSelect allowClear value={ev.EV_Manufacturer_Name || undefined} placeholder="Select"
                      options={vehicleManufacturers.map(v => ({ label: v, value: v }))}
                      onChange={(v) => { const isClear = !v;
                        updateEV(index, { EV_Manufacturer_Name: v || '',  EV_Model_Name: '',  Daily_kWh: 0,  Annual_kWh: 0,
                          ...(isClear && {Avg_DailyMiles_Driven: 0, Home_Charging_Perc: 0 }) }); }}/>
                  </div>

                  <div className="obd-load-calc__field">
                    <label className="obd-load-ev_label">Model</label>
                    <OBDSelect allowClear value={ev.EV_Model_Name || undefined} placeholder="Select"
                      options={(evModels[index] || []).map(m => ({ label: m, value: m }))}
                      onChange={(v) => updateEV(index, {EV_Model_Name: v || '', Daily_kWh: 0, Annual_kWh: 0 })}/>
                  </div>

                  <div className="obd-load-calc__field">
                    <label className="obd-load-ev_label">Avg Daily Miles</label>
                    <Input type="number" value={ev.Avg_DailyMiles_Driven}
                      onChange={(e) => updateEV(index, { Avg_DailyMiles_Driven: Number(e.target.value) }) } />
                  </div>

                  <div className="obd-load-calc__field">
                    <label className="obd-load-ev_label">Annual Drives (Miles)</label>
                    <Input value={ev.Annual_Miles} disabled />
                  </div>

                  <div className="obd-load-calc__field">
                    <label className="obd-load-ev_label">Daily Charged Home (%)</label>
                    <OBDSelect value={String(ev.Home_Charging_Perc)}
                      options={percentChargedOptions.map(p => ({ label: p, value: p }))}
                      onChange={(v) => updateEV(index, { Home_Charging_Perc: Number(v) }) } />
                  </div>

                  <div className="obd-load-calc__field">
                    <label className="obd-load-ev_label">Est. Daily Usage (kWh)</label>
                    <Input value={ev.Daily_kWh.toFixed(3)} disabled />
                  </div>

                  <div className="obd-load-calc__field">
                    <label className="obd-load-ev_label">Est. Annual Usage (kWhs)</label>
                    <Input value={ev.Annual_kWh.toFixed(3)} disabled />
                  </div>

                </div>
                </Tabs.TabPane>
              ))}
            </Tabs>

          </div>
        </div>
      )}
    </DashboardModal>
  );
};

export default UtilityLoadCalculatorModal;